import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  AccountTree as PedigreeIcon,
  Add as AddIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DatabaseService from '../services/database';
import { useLicense } from '../context/LicenseContext';
import { getBirdDisplayName } from '../utils/birdSearch';
import { handleNotesPaste } from '../utils/pastePlainText';

// Label a parent cell by the parent's name, falling back to its ring number
// (many birds are ring-only), or an em dash when no parent is set.
const parentLabel = (id, name, ringNumber) =>
  id ? getBirdDisplayName({ name, ring_number: ringNumber }) : '—';

const TestPedigrees = () => {
  const navigate = useNavigate();
  const { state: licenseState, openPrompt } = useLicense();
  // Test Pedigrees is a paid feature. Full and past_due (paying, in grace)
  // installs get it; trial / read-only / unlicensed installs see an upgrade
  // notice but can still open the page.
  const isPaidFeatureUnlocked =
    licenseState && (licenseState.mode === 'full' || licenseState.mode === 'past_due');
  const [testBirds, setTestBirds] = useState([]);
  const [allBirds, setAllBirds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);

  const initialForm = () => ({
    name: '',
    ring_letters: '',
    ring_numbers: '',
    ring_number: '',
    sex: '',
    color: '',
    breed: '',
    year: '',
    notes: '',
    sire_id: null,
    dam_id: null
  });

  const [formData, setFormData] = useState(initialForm());

  useEffect(() => {
    loadTestBirds();
  }, []);

  const loadTestBirds = async () => {
    try {
      const [test, all] = await Promise.all([
        DatabaseService.getTestBirds(),
        DatabaseService.getBirdsForAutocomplete('', true)
      ]);
      setTestBirds(test);
      setAllBirds(all);
    } catch (err) {
      console.error('Error loading test birds:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'ring_letters' || field === 'ring_numbers') {
        const letters = field === 'ring_letters' ? value : prev.ring_letters;
        const numbers = field === 'ring_numbers' ? value : prev.ring_numbers;
        updated.ring_number = letters && numbers ? `${letters} ${numbers}` : `${letters}${numbers}`;
      }
      return updated;
    });
    setError('');
  };

  const handleParentSelect = (field, bird) => {
    setFormData(prev => ({
      ...prev,
      [field]: bird ? bird.id : null
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const birdData = {
        ...formData,
        name: formData.name || 'Test Bird',
        is_test: true
      };
      await DatabaseService.createBird(birdData);
      setSuccess('Test bird created successfully!');
      setFormData(initialForm());
      setShowForm(false);
      await loadTestBirds();
    } catch (err) {
      console.error('Error creating test bird:', err);
      setError(err.isLicenseError ? err.message : 'Error creating test bird. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (birdId) => {
    if (!window.confirm('Delete this test bird?')) return;
    try {
      await DatabaseService.deleteBird(birdId);
      await loadTestBirds();
      setSuccess('Test bird deleted.');
    } catch (err) {
      console.error('Error deleting test bird:', err);
      setError('Error deleting test bird.');
    }
  };

  const getMaleOptions = () => allBirds.filter(b => b.sex === 'male');
  const getFemaleOptions = () => allBirds.filter(b => b.sex === 'female');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading test pedigrees...</Typography>
      </Box>
    );
  }

  // Paid-feature gate: reachable page, but the tool itself is locked until the
  // user is on a paid plan.
  if (!isPaidFeatureUnlocked) {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
          Test Pedigrees
        </Typography>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Box
              sx={{
                width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'primary.main', color: 'primary.contrastText'
              }}
            >
              <LockIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              A full-version feature
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
              Test Pedigrees lets you create throwaway sample birds to experiment with
              pedigree layouts — separate from your real records. It's included with any
              paid plan (Monthly, Annual, or Lifetime).
            </Typography>
            {(licenseState?.mode === 'trial' || licenseState?.mode === 'unlicensed') && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ mb: 2 }}>
                  You can create real pedigrees with your free trial. Start by adding a pigeon to your loft.
                </Typography>
                <Button variant="contained" size="large"
                  onClick={() => licenseState.mode === 'unlicensed' ? openPrompt() : navigate('/birds/add')}>
                  {licenseState.mode === 'unlicensed' ? 'Start my free trial' : 'Create a pedigree with my trial'}
                </Button>
              </Box>
            )}
            <Button variant="outlined" size="large" onClick={openPrompt}>
              View upgrade options
            </Button>
            {licenseState && licenseState.mode === 'trial' && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                Your trial includes up to 60 birds and 3 pedigree PDF downloads.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Test Pedigrees
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setShowForm(!showForm); setError(''); setSuccess(''); }}
        >
          {showForm ? 'Cancel' : 'Add Test Bird'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Test birds are for creating quick test pedigrees. No fields are required.
          These birds are separate from your real pigeon records.
        </Typography>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {showForm && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              New Test Bird
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Optional"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Ring Letters"
                  value={formData.ring_letters}
                  onChange={(e) => handleInputChange('ring_letters', e.target.value.toUpperCase())}
                  placeholder="e.g., BE"
                  size="small"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Ring Numbers"
                  value={formData.ring_numbers}
                  onChange={(e) => handleInputChange('ring_numbers', e.target.value)}
                  placeholder="e.g., 001"
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sex</InputLabel>
                  <Select
                    value={formData.sex}
                    label="Sex"
                    onChange={(e) => handleInputChange('sex', e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="male">Cock</MenuItem>
                    <MenuItem value="female">Hen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value ? parseInt(e.target.value) : '')}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Strain"
                  value={formData.breed}
                  onChange={(e) => handleInputChange('breed', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={getMaleOptions()}
                  value={allBirds.find(b => b.id === formData.sire_id) || null}
                  getOptionLabel={(option) => `${option.name || option.ring_number || 'Unnamed'} (${option.ring_number || 'No ring'})`}
                  onChange={(e, val) => handleParentSelect('sire_id', val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Sire (Father)" size="small" placeholder="Search cocks..." />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 1, width: 24, height: 24, bgcolor: 'info.main', fontSize: 14 }}>
                        <MaleIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">{option.name || option.ring_number || 'Unnamed'}</Typography>
                    </Box>
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={getFemaleOptions()}
                  value={allBirds.find(b => b.id === formData.dam_id) || null}
                  getOptionLabel={(option) => `${option.name || option.ring_number || 'Unnamed'} (${option.ring_number || 'No ring'})`}
                  onChange={(e, val) => handleParentSelect('dam_id', val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Dam (Mother)" size="small" placeholder="Search hens..." />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 1, width: 24, height: 24, bgcolor: 'secondary.main', fontSize: 14 }}>
                        <FemaleIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2">{option.name || option.ring_number || 'Unnamed'}</Typography>
                    </Box>
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  onPaste={(e) => handleNotesPaste(e, formData.notes, (v) => handleInputChange('notes', v))}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Test Bird'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {testBirds.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PedigreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No test birds yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Test Bird" to create birds for testing pedigree layouts.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Sire</strong></TableCell>
                <TableCell><strong>Dam</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testBirds.map((bird) => (
                <TableRow key={bird.id} hover>
                  <TableCell>{bird.name || '—'}</TableCell>
                  <TableCell>{parentLabel(bird.sire_id, bird.sire_name, bird.sire_ring_number)}</TableCell>
                  <TableCell>{parentLabel(bird.dam_id, bird.dam_name, bird.dam_ring_number)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/pedigree/${bird.id}`)}
                      title="View Pedigree"
                    >
                      <PedigreeIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(bird.id)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TestPedigrees;
